import { useQueryClient } from '@tanstack/react-query'
import { useLocalSearchParams } from 'expo-router'
import { useEffect, useState } from 'react'
import { Alert, Linking, Platform, ScrollView, StyleSheet, View } from 'react-native'
import { ActivityIndicator, Chip, Divider, Text, useTheme } from 'react-native-paper'
import { useSemanticColors } from '@/constants/SemanticColors'
import { useCurrentOrg } from '@/hooks/useCurrentOrg'
import { useUsage } from '@/hooks/useUsage'
import { createCheckoutSession, createPortalSession, formatPrice, isUnlimited } from '@/lib/billing'
import { captureException } from '@/lib/errors'
import { notify } from '@/lib/notice'
import type { BillingInfo, SubscriptionStatus } from '@/types/billing'
import { Button } from '@/ui/Button'
import { PricingTable, UsageBar } from '@/ui/billing'
import { Card } from '@/ui/Card'
import { Page } from '@/ui/Page'

const isIOS = Platform.OS === 'ios'

const statusLabels: Record<SubscriptionStatus, string> = {
    active: 'Active',
    trialing: 'Trial',
    past_due: 'Past Due',
    canceled: 'Canceled',
    incomplete: 'Incomplete',
}

const statusColorKeys: Record<SubscriptionStatus, 'primary' | 'tertiary' | 'error' | 'outline'> = {
    active: 'primary',
    trialing: 'tertiary',
    past_due: 'error',
    canceled: 'outline',
    incomplete: 'outline',
}

function PlanLimitsSection({
    isVisible,
    limits,
}: {
    isVisible: boolean
    limits?: BillingInfo['limits']
}) {
    if (!isVisible || !limits) return null

    return (
        <>
            <Divider style={styles.divider} />
            <View style={styles.limits}>
                <Text variant="labelMedium" style={styles.limitLabel}>
                    {isUnlimited(limits.active_team_members)
                        ? 'Unlimited'
                        : limits.active_team_members}{' '}
                    team members
                </Text>
                <Text variant="labelMedium" style={styles.limitLabel}>
                    {isUnlimited(limits.active_jobs) ? 'Unlimited' : limits.active_jobs} active jobs
                </Text>
                <Text variant="labelMedium" style={styles.limitLabel}>
                    {isUnlimited(limits.active_customers) ? 'Unlimited' : limits.active_customers}{' '}
                    customers
                </Text>
            </View>
        </>
    )
}

function PlanFeaturesSection({
    isVisible,
    features,
}: {
    isVisible: boolean
    features: string[] | undefined
}) {
    const semanticColors = useSemanticColors()

    if (!isVisible || !features || features.length === 0) return null

    return (
        <>
            <Divider style={styles.divider} />
            <View style={styles.features}>
                {features.map(feature => (
                    <View key={feature} style={styles.featureRow}>
                        <Text style={[styles.checkmark, { color: semanticColors.successText }]}>
                            {'\u2713'}
                        </Text>
                        <Text variant="bodySmall" style={styles.featureText}>
                            {feature}
                        </Text>
                    </View>
                ))}
            </View>
        </>
    )
}

function TrialBanner({ isVisible, trialEnd }: { isVisible: boolean; trialEnd?: string | null }) {
    const theme = useTheme()

    if (!isVisible || !trialEnd) return null

    return (
        <View style={[styles.trialBanner, { backgroundColor: theme.colors.tertiaryContainer }]}>
            <Text variant="bodyMedium" style={{ color: theme.colors.onTertiaryContainer }}>
                Your trial ends on {new Date(trialEnd).toLocaleDateString()}
            </Text>
        </View>
    )
}

function CancellationBanner({ isVisible }: { isVisible: boolean }) {
    const theme = useTheme()

    if (!isVisible) return null

    return (
        <View style={[styles.trialBanner, { backgroundColor: theme.colors.errorContainer }]}>
            <Text variant="bodySmall" style={{ color: theme.colors.onErrorContainer }}>
                Your subscription will be canceled at the end of the billing period
            </Text>
        </View>
    )
}

function PlanDetailsCard({
    billing,
    actionLoading,
    onManageBilling,
}: {
    billing: BillingInfo | undefined
    actionLoading: boolean
    onManageBilling: () => void
}) {
    const theme = useTheme()
    const { orgSlug } = useLocalSearchParams<{ orgSlug: string }>()
    const isFree = billing?.plan?.id === 'free'
    const isTrialing = billing?.subscription?.status === 'trialing'

    return (
        <Card>
            <Card.Content>
                <Text variant="titleLarge" style={styles.sectionTitle}>
                    Current Plan
                </Text>
                <View style={styles.planHeader}>
                    <Text variant="titleMedium" style={styles.planName}>
                        {billing?.plan?.name ?? 'Free'}
                    </Text>
                    {billing?.subscription?.status &&
                        billing.subscription.status !== 'incomplete' && (
                            <Chip
                                compact
                                mode="flat"
                                style={{
                                    backgroundColor:
                                        theme.colors[statusColorKeys[billing.subscription.status]],
                                }}
                                textStyle={{ color: theme.colors.onPrimary }}
                            >
                                {statusLabels[billing.subscription.status]}
                            </Chip>
                        )}
                </View>

                {billing?.plan?.description && (
                    <Text variant="bodyMedium" style={{ color: theme.colors.onSurfaceVariant }}>
                        {billing.plan.description}
                    </Text>
                )}

                <View style={styles.priceRow}>
                    {billing?.plan?.price_monthly ? (
                        <>
                            <Text variant="headlineMedium" style={styles.price}>
                                {formatPrice(billing.plan.price_monthly)}
                            </Text>
                            <Text
                                variant="bodyMedium"
                                style={{ color: theme.colors.onSurfaceVariant }}
                            >
                                /month
                            </Text>
                        </>
                    ) : (
                        <Text variant="headlineMedium" style={styles.price}>
                            Free
                        </Text>
                    )}
                </View>

                <PlanLimitsSection isVisible={!!billing?.limits} limits={billing?.limits} />

                <PlanFeaturesSection
                    isVisible={!!billing?.plan?.features && billing.plan.features.length > 0}
                    features={billing?.plan?.features}
                />

                <TrialBanner isVisible={isTrialing} trialEnd={billing?.subscription?.trial_end} />

                <CancellationBanner isVisible={!!billing?.subscription?.cancel_at_period_end} />

                {!isFree && (
                    <Button
                        title="Manage Billing"
                        mode="outlined"
                        onPress={
                            isIOS
                                ? () =>
                                      Linking.openURL(
                                          `https://jobtimey.com/${orgSlug}/settings/billing`
                                      )
                                : onManageBilling
                        }
                        disabled={actionLoading}
                        style={styles.manageButton}
                    />
                )}
            </Card.Content>
        </Card>
    )
}

export default function BillingSettingsPage() {
    const theme = useTheme()
    const queryClient = useQueryClient()
    const { orgSlug, success } = useLocalSearchParams<{ orgSlug: string; success?: string }>()
    const { role } = useCurrentOrg({ throwIfNotFound: false })
    const { data: billing, isLoading } = useUsage()
    const [actionLoading, setActionLoading] = useState(false)

    useEffect(() => {
        if (success === 'true') {
            notify.success('Successfully updated plan')
            queryClient.invalidateQueries({ queryKey: ['billing', 'usage', orgSlug] })
        }
    }, [success, orgSlug, queryClient])

    const isAdmin = role === 'admin'

    if (!isAdmin) {
        return (
            <Page>
                <Card>
                    <Card.Content>
                        <Text variant="bodyLarge">
                            Only administrators can manage billing settings.
                        </Text>
                    </Card.Content>
                </Card>
            </Page>
        )
    }

    const handleUpgrade = async (planId: string) => {
        if (!orgSlug) return
        try {
            setActionLoading(true)
            const result = await createCheckoutSession(planId, orgSlug)
            if (result.updated) {
                await queryClient.invalidateQueries({ queryKey: ['billing', 'usage', orgSlug] })
                Alert.alert(
                    'Plan Updated',
                    'Your plan has been changed. Prorated charges will apply.'
                )
            } else if (result.url) {
                await Linking.openURL(result.url)
            }
        } catch (error) {
            captureException('Failed to change plan', error)
        } finally {
            setActionLoading(false)
        }
    }

    const handleManageBilling = async () => {
        if (!orgSlug) return
        try {
            setActionLoading(true)
            const { url } = await createPortalSession(orgSlug)
            await Linking.openURL(url)
        } catch (error) {
            captureException('Failed to create portal session', error)
        } finally {
            setActionLoading(false)
        }
    }

    if (isLoading) {
        return (
            <Page>
                <View style={styles.loading}>
                    <ActivityIndicator size="large" />
                    <Text variant="bodyMedium">Loading billing info...</Text>
                </View>
            </Page>
        )
    }

    return (
        <Page>
            <ScrollView
                contentContainerStyle={styles.scrollContent}
                showsVerticalScrollIndicator={false}
            >
                <PlanDetailsCard
                    billing={billing}
                    actionLoading={actionLoading}
                    onManageBilling={handleManageBilling}
                />

                <Card style={styles.usageCard}>
                    <Card.Content>
                        <Text variant="titleLarge" style={styles.sectionTitle}>
                            Usage
                        </Text>
                        <View style={styles.usageRow}>
                            <UsageBar type="team" label="Team Members" />
                            <UsageBar type="jobs" label="Active Jobs" />
                            <UsageBar type="customers" label="Customers" />
                        </View>
                    </Card.Content>
                </Card>

                <Card style={styles.plansCard}>
                    <Card.Content>
                        <Text variant="titleLarge" style={styles.sectionTitle}>
                            Change Your Plan
                        </Text>
                        <Text
                            variant="bodyMedium"
                            style={{
                                color: theme.colors.onSurfaceVariant,
                                marginBottom: 16,
                            }}
                        >
                            {isIOS
                                ? 'To change your plan, visit our website where you can view all available plans and manage your subscription.'
                                : 'Choose the plan that best fits your team.'}
                        </Text>
                        {isIOS ? (
                            <Button
                                title="View Plans on jobtimey.com"
                                mode="contained"
                                onPress={() =>
                                    Linking.openURL(
                                        `https://jobtimey.com/${orgSlug}/settings/billing`
                                    )
                                }
                            />
                        ) : (
                            <PricingTable
                                currentPlanId={billing?.plan?.id ?? 'free'}
                                usage={billing?.usage}
                                onSelectPlan={handleUpgrade}
                            />
                        )}
                    </Card.Content>
                </Card>
            </ScrollView>
        </Page>
    )
}

const styles = StyleSheet.create({
    scrollContent: {
        paddingBottom: 24,
    },
    loading: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        gap: 16,
    },
    planHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 8,
    },
    sectionTitle: {
        fontWeight: '600',
        marginBottom: 16,
    },
    priceRow: {
        flexDirection: 'row',
        alignItems: 'baseline',
        gap: 4,
        marginTop: 12,
    },
    planName: {
        fontWeight: '600',
    },
    price: {
        fontWeight: 'bold',
    },
    divider: {
        marginVertical: 16,
    },
    limits: {
        gap: 4,
    },
    limitLabel: {
        fontWeight: '500',
    },
    features: {
        gap: 8,
    },
    featureRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 8,
    },
    checkmark: {
        fontWeight: 'bold',
    },
    featureText: {
        flex: 1,
    },
    trialBanner: {
        marginTop: 16,
        padding: 12,
        borderRadius: 8,
    },
    manageButton: {
        marginTop: 16,
    },
    usageRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 16,
    },
    usageCard: {
        marginTop: 16,
    },
    plansCard: {
        marginTop: 16,
    },
})
